/**
 * The tenant's Demo Connections, read from
 * `GET /api/admin/tenants/{id}/demo-connections`.
 *
 * WHAT THIS SCREEN REPLACED. A card that fetched a flat connection list and
 * rendered "3 of 5 connected" — one axis, no provenance, and no way to tell
 * "the runtime says two are missing" from "the runtime did not answer". Beside
 * it, the Create screen promised five connection names from a constant. The two
 * together are the defect this programme exists to close: DESIRED state
 * rendered as though it were ACTUAL.
 *
 * THE LAYOUT IS THE ARGUMENT. Three panels, side by side from `md` and stacked
 * on a phone, in the order the facts were produced:
 *
 *     APPROVED (desired) → ATTEMPTED (job) → CONNECTED NOW (actual)
 *
 * They are drawn as three separate surfaces, never as one merged verdict,
 * because the failure mode is a reader carrying a fact across the boundary —
 * "five were approved" becoming "five are connected". A single combined tile
 * would do that carrying for them.
 *
 * DIRECTION A, APPLIED
 *   · One piece of AREA identity artwork, at identity-lg, in the header: this
 *     section IS the Connections area seen from a tenant, and it reuses that
 *     area's mark rather than commissioning a second silhouette for the same
 *     subject.
 *   · One PROVIDER mark per item row, at identity-sm — the same Cluvik
 *     stand-in the Create screen's review draws, from the same module, so the
 *     two screens that show the same three providers show them identically. A
 *     provider is an identity, so it carries artwork; only where the runtime
 *     actually named one.
 *   · Every other glyph is flat, monochrome, lucide, one stroke weight, 16px.
 *     Panel headings and the Re-check control are not places.
 *   · No gradient tiles on non-places, no improvised CSS depth, no redrawn
 *     provider logos.
 *   · Colour comes from semantic tokens only (`--success`, `--warning`,
 *     `--destructive`, `--muted`, `--border`, `--card`), which is what makes
 *     the whole card render correctly in dark without a second stylesheet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle2, ClipboardCheck, Loader2, OctagonAlert,
  PlayCircle, RefreshCw, Radar, ShieldAlert,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import StatusBadge from '../../../components/admin/StatusBadge';
import IdentityMark from '../../../design-system/identity/IdentityMark';
import ProviderMark from '../../../design-system/identity/ProviderMark';
import { describeApiError } from '../../../utils/apiError';
import { describeTenantDemoConnections } from './demoConnectionsPresentation';
import ManagedServicesChips from './ManagedServicesChips';
import { MANAGED_SERVICES_NOTE, describeManagedServices } from './managedServices';
import DemoConnectionsSwitch from './DemoConnectionsSwitch';

const API = process.env.REACT_APP_BACKEND_URL;

/** Severity -> the surface the headline is drawn on. Three tiers, never two:
 *  blocked, unknown ("we could not look") and warning. `unknown` folded into
 *  either neighbour is the defect this screen exists to expose. */
const SEVERITY_SURFACE = {
  blocked: 'border-destructive-text/40 bg-destructive/5',
  unknown: 'border-warning/40 bg-warning/10',
  warning: 'border-warning/30 bg-warning/5',
  ok: 'border-border bg-muted/30',
};

const SEVERITY_ICON = {
  blocked: { Icon: OctagonAlert, className: 'text-destructive-text' },
  unknown: { Icon: AlertTriangle, className: 'text-warning-text' },
  warning: { Icon: AlertTriangle, className: 'text-warning-text' },
  ok: { Icon: CheckCircle2, className: 'text-success' },
};

/** Tone -> the words' colour. Semantic tokens only; no slate, no indigo. */
const TONE_TEXT = {
  success: 'text-success',
  warning: 'text-warning-text',
  destructive: 'text-destructive-text',
  sky: 'text-info-text',
  muted: 'text-muted-foreground',
};

/**
 * A count, or the absence of one.
 *
 * `null` renders an em dash and the words "not measured" — NEVER `0`. A zero
 * states a measurement nobody made, and it is the single easiest thing to get
 * wrong here: `{count ?? 0}` reads as defensive coding and is a fabrication.
 */
const Count = ({ value, label, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <p className={`text-2xl font-semibold tabular-nums ${value === null ? 'text-muted-foreground' : 'text-foreground'}`}>
      {value === null ? '—' : value}
    </p>
    <p className="text-xs text-muted-foreground">{value === null ? `${label} · not measured` : label}</p>
  </div>
);

/** One row of a panel's facts. Absent values say so rather than rendering blank. */
const Fact = ({ label, value, mono, testId }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1" data-testid={testId}>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className={`min-w-0 break-all text-right text-xs ${value ? 'text-foreground' : 'text-muted-foreground'} ${mono && value ? 'font-mono' : ''}`}>
      {value || 'Not reported'}
    </dd>
  </div>
);

/**
 * One of the three concepts.
 *
 * A bordered surface of its own, with its own badge and its own one-sentence
 * meaning. The meaning line is not decoration: "Recorded", "Completed" and
 * "Reported" are three different claims about three different things, and a
 * reader who has only the three words will merge them.
 */
const ConceptPanel = ({ icon: Glyph, eyebrow, title, badge, meaning, testId, children }) => (
  <section
    className="flex min-w-0 flex-col rounded-xl border border-border bg-muted/20 p-4"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {/* Flat monochrome, 16px. A heading marker, never artwork. */}
          <Glyph className="h-4 w-4 shrink-0" aria-hidden="true" />
          {eyebrow}
        </p>
        <h4 className="mt-1 text-sm font-medium text-foreground">{title}</h4>
      </div>
      {badge}
    </div>
    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{meaning}</p>
    {children}
  </section>
);

/** The list of items, or an honest sentence about why there is none. */
const ItemList = ({ items, testIdPrefix }) => (
  <ul className="divide-y divide-border rounded-xl border border-border" data-testid={`${testIdPrefix}-list`}>
    {items.map((item) => (
      <li
        key={`${item.provider || 'unknown'}:${item.connection || item.label}`}
        className="grid grid-cols-1 gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
        data-testid={`${testIdPrefix}-${item.connection || item.provider}`}
      >
        <div className="flex min-w-0 items-start gap-3">
          {/* THE PROVIDER'S MARK, and only where the runtime NAMED a provider.
              The same Cluvik stand-in the Create screen's review draws — see
              ProviderMark.jsx for why this repository holds no licensed brand
              artwork and will not redraw one from memory.

              `item.provider` is nullable on this endpoint, and a null one gets
              NO mark rather than a '?' tile. Artwork for a provider nobody
              named is the same fabrication as a `?? 0` on a count nobody
              measured, drawn instead of written. The spacer is identity-sm's
              own 22px, so the text edge of every row lands on one line and the
              absence reads as an absence rather than as a ragged list.

              Decorative: the provider id is written one element away. */}
          {item.provider
            ? (
              <ProviderMark
                provider={item.provider}
                label={item.provider}
                size="sm"
                className="mt-0.5 shrink-0"
              />
            )
            : <span className="mt-0.5 h-[22px] w-[22px] shrink-0" aria-hidden="true" />}
          <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{item.label}</p>
          <p className="mt-0.5 break-all text-xs text-muted-foreground">
            {item.connection || 'no connection key'}
            {item.provider ? ` · ${item.provider}` : ''}
          </p>
          {/* The runtime's own word, verbatim beside the classified chip. An
              unrecognised status is legible here rather than only counted. */}
          {item.actualStatus && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              runtime status: <span className="font-mono">{item.actualStatus}</span>
            </p>
          )}
          {item.detail && <p className="mt-1 break-words text-xs text-muted-foreground">{item.detail}</p>}
          {!item.outcomeMapped && (
            <p className="mt-1 text-xs text-warning-text">
              This build does not know this outcome value. It is shown as-is rather than
              mapped onto one that looks similar.
            </p>
          )}
          </div>
        </div>
        <div className="sm:text-right">
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <StatusBadge status={item.badgeStatus} label={item.outcomeLabel} />
            {/* WHAT THE APPROVAL SAID, on its own axis. Neutral in every case:
                `required` is not bad news and `optional` is not good news, and
                toning this pill would dress a signed property as health.
                "Not stated" is drawn as its own third thing — never as
                Optional, which would put a decision on screen that nobody
                took. */}
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${
                item.requirementStated
                  ? 'border-border text-foreground'
                  : 'border-dashed border-border text-muted-foreground'}`}
              data-testid={`demo-requirement-${item.connection || item.provider}`}
              title={item.requirementMeaning}
            >
              {item.requirementLabel}
            </span>
          </div>
          <p className="mt-1 max-w-xs text-xs text-muted-foreground sm:ml-auto">{item.outcomeMeaning}</p>
          {/* ITEM 18: configured and enabled are two facts. The chip above is
              the credential; this line is the consuming feature, by that
              feature's own rule. Not enabled is an operator action and is
              toned warning, never destructive. Not reported stays muted and
              says so — it is never drawn as off, and never as on. */}
          <p
            className={`mt-1 max-w-xs text-xs sm:ml-auto ${TONE_TEXT[item.featureTone] || TONE_TEXT.muted}`}
            data-testid={`demo-feature-${item.connection || item.provider}`}
            title={item.featureMeaning}
          >
            {item.featureLabel}
            {item.featureEnabled === false && item.featureDetail ? ` — ${item.featureDetail}` : ''}
          </p>
          {/* THE CONSEQUENCE, and only where there is one to draw. A required
              member failing and an optional member failing have different
              costs, and this is the one place the screen makes them look
              different — under a member that is ACTUALLY failing, rather than
              by colouring the approval itself.

              It states the RULE ("a tenant"), never a verdict about this one:
              whether THIS tenant is below Ready is `readiness_block`'s answer
              and nothing else's. */}
          {item.outcome === 'attention' && (
            <p
              className={`mt-1 max-w-xs text-xs sm:ml-auto ${TONE_TEXT[item.requirementFailingTone] || TONE_TEXT.muted}`}
              data-testid={`demo-consequence-${item.connection || item.provider}`}
            >
              {item.requirementConsequence}
            </p>
          )}
          {!item.requirementMapped && item.requirement !== null && (
            <p className="mt-1 max-w-xs text-xs text-warning-text sm:ml-auto">
              {item.requirementMeaning}
            </p>
          )}
        </div>
      </li>
    ))}
  </ul>
);

/**
 * The presentational half, exported so a render test can mount it against a
 * fixture without a network stub.
 */
export function DemoConnectionsView({ view, onRefresh, managedServices, control }) {
  const { Icon: SeverityIcon, className: severityClass } = SEVERITY_ICON[view.severity] || SEVERITY_ICON.ok;
  const busy = view.state === 'loading';

  return (
    <div
      className="mb-4 overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
      data-testid="demo-connections-card"
    >
      <div className="border-b border-border bg-gradient-to-r from-muted/40 to-card px-5 pb-4 pt-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {/* The one piece of identity artwork on this card. */}
            <IdentityMark path="/connections/profiles" size="lg" className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-foreground">Demo Connections</h3>
              <p className="text-xs text-muted-foreground">
                What was approved, what provisioning tried, and what is connected right now.
              </p>
            </div>
          </div>
          {onRefresh && (
            /* An ACTION. Flat, monochrome, one stroke weight — never artwork. */
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={busy}
              data-testid="demo-connections-recheck"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
              Re-check
            </Button>
          )}
        </div>

        {/* INCLUDED PLATFORM SERVICES — a line of their own, ABOVE the three
            panels and outside them. Managed storage and email are not
            connection rows, not demo connections, and not anything the
            panels below measure; listing them here is what stops "two
            connected" from being read as "storage is off". Rendered from the
            tenant read's `managed_services` when the page has it, and from
            the platform rule when it does not: an older read that omits the
            field has not made the services go away. */}
        <div className="mt-4 border-t border-border/60 pt-3" data-testid="demo-connections-managed-services">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Included platform services
          </p>
          <div className="mt-2">
            <ManagedServicesChips services={describeManagedServices(managedServices)} testIdPrefix="tenant-managed-service" />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{MANAGED_SERVICES_NOTE}</p>
        </div>

        {/* The ON/OFF control, when the host page supplied one. Sits under the
            services line so the thing an operator can change is visibly a
            different thing from the services they cannot. */}
        {control && (
          <div className="mt-3" data-testid="demo-connections-control">
            {control}
          </div>
        )}
      </div>

      <div className="px-5 py-5 sm:px-6">
        {/* ── the one headline, at the severity that was measured ─────── */}
        <div
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${SEVERITY_SURFACE[view.severity] || SEVERITY_SURFACE.ok}`}
          data-testid={`demo-connections-severity-${view.severity}`}
          role={view.severity === 'blocked' ? 'alert' : undefined}
        >
          {busy
            ? <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
            : <SeverityIcon className={`mt-0.5 h-5 w-5 shrink-0 ${severityClass}`} aria-hidden="true" />}
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground" data-testid="demo-connections-headline">
              {view.headline}
            </p>
            {view.detail && (
              <p className="mt-1 break-words text-sm text-muted-foreground" data-testid="demo-connections-detail">
                {view.detail}
              </p>
            )}

            {/* ── the gate that is holding the tenant below Ready ───────
                NESTED INSIDE THE HEADLINE, not beside it. The first draft drew
                this as its own strip under the banner and the two printed the
                SAME server sentence back to back — the screen said one thing
                twice and neither instance carried more weight. That is the
                exact defect already recorded against the Create screen's
                blocking block, and the fix there is the fix here: one block,
                one tone, and the nested part carries only what the banner does
                not already say.

                The tone is the measured severity. `blocked` is honest — the
                tenant genuinely cannot reach Ready — and REPAIRABLE is carried
                by the words, because it is a claim about time rather than
                about severity. */}
            {view.readinessBlock && (
              <div
                className="mt-3 border-t border-border/60 pt-3"
                data-testid="demo-readiness-block"
              >
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-foreground">
                  <ShieldAlert className={`h-4 w-4 shrink-0 ${severityClass}`} aria-hidden="true" />
                  {view.readinessBlock.label}
                  {view.readinessBlock.profileId && (
                    <span className="font-mono text-xs font-normal text-muted-foreground">
                      {view.readinessBlock.profileId}
                    </span>
                  )}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Gate <span className="font-mono">{view.readinessBlock.code || 'unnamed'}</span>
                  {view.readinessBlock.reason ? ` · ${view.readinessBlock.reason}` : ''}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Repairable — the approval is intact, nothing has been discarded, and this
                  clears on its own when the profile resolves.
                </p>
              </div>
            )}
          </div>
        </div>

        {view.state === 'ready' && (
          <>
            {/* ── the three concepts, kept visibly apart ──────────────── */}
            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="demo-connections-concepts">
              <ConceptPanel
                icon={ClipboardCheck}
                eyebrow="Approved"
                title="What the operator selected"
                testId="demo-concept-desired"
                meaning={view.desired.meaning}
                badge={<StatusBadge status={view.desired.badge} label={view.desired.label} />}
              >
                <dl className="mt-3 divide-y divide-border/60 border-t border-border/60">
                  {/* NULL UNLESS RECORDED. "Not recorded" is a different fact
                      from "zero connections", and this is where they get
                      confused if a `?? 0` ever creeps in. */}
                  <Fact
                    label="Connections approved"
                    value={view.desired.itemCount}
                    testId="demo-desired-item-count"
                  />
                  <Fact label="Profile" value={view.desired.profileLabel || view.desired.profileId} mono={!view.desired.profileLabel} />
                  {/* WHICH APPROVED DEFINITION. A different question from the
                      digest below, so a different row — merging them would make
                      "a revision" and "an approval altered after signing" look
                      like one fact. */}
                  <Fact
                    label="Definition"
                    value={view.desired.definition}
                    mono
                    testId="demo-desired-definition"
                  />
                  {/* ARE THESE EXACT FROZEN ITEMS UNCHANGED. The algorithm is
                      named beside it because a SCHEMA CHANGE MUST NOT READ AS
                      TAMPERING: v2 added each member's requirement, which moves
                      every digest for a reason that is not tampering. Nothing
                      in this UI compares fingerprints, and nothing may compare
                      them across algorithms. */}
                  <Fact label="Fingerprint" value={view.desired.profileFingerprint} mono testId="demo-desired-fingerprint" />
                  {view.desired.fingerprintAlgorithm && (
                    <p
                      className="py-1 text-xs text-muted-foreground"
                      data-testid="demo-desired-fingerprint-algorithm"
                    >
                      {view.desired.fingerprintAlgorithm}
                    </p>
                  )}
                  <Fact label="Frozen at" value={view.desired.frozenAt} />
                  {/* THE THREE BUCKETS, and `Not stated` is one of them.
                      Folding it into Optional would report decisions nobody
                      took — which is the whole reason the field is an enum. */}
                  {view.requirementCounts && view.requirementCounts.total > 0 && (
                    <div className="pt-2" data-testid="demo-requirement-mix">
                      <p className="text-xs text-muted-foreground">Of those approved</p>
                      <p className="mt-0.5 text-xs text-foreground">
                        {view.requirementCounts.required} required
                        {' · '}{view.requirementCounts.optional} optional
                        {' · '}{view.requirementCounts.notStated} not stated
                      </p>
                    </div>
                  )}
                  {view.desired.reason && (
                    <div className="pt-2" data-testid="demo-desired-reason">
                      <p className={`text-xs ${TONE_TEXT[view.desired.tone] || TONE_TEXT.muted}`}>
                        Reason: {view.desired.reason}
                      </p>
                    </div>
                  )}
                </dl>
              </ConceptPanel>

              <ConceptPanel
                icon={PlayCircle}
                eyebrow="Attempted"
                title="What provisioning tried"
                testId="demo-concept-attempted"
                meaning={view.attempted.meaning}
                badge={<StatusBadge status={view.attempted.badge} label={view.attempted.label} />}
              >
                <dl className="mt-3 divide-y divide-border/60 border-t border-border/60">
                  <Fact label="Job" value={view.attempted.jobId} mono />
                  <Fact label="Job status" value={view.attempted.jobStatus} />
                  {/* Attempts on THIS row, not attempts ever: a fresh budget is
                      a new job row rather than a rewritten field. */}
                  <Fact
                    label="Retries on this job"
                    value={view.attempted.retryCount === null ? null
                      : `${view.attempted.retryCount}${view.attempted.maxRetries === null ? '' : ` of ${view.attempted.maxRetries}`}`}
                  />
                  <Fact label="Last update" value={view.attempted.updatedAt} />
                  {view.attempted.lastError && (
                    <div className="pt-2">
                      <p className="break-words text-xs text-destructive-text" data-testid="demo-attempted-error">
                        {view.attempted.lastError}
                      </p>
                    </div>
                  )}
                </dl>
                <p className="mt-3 text-xs text-muted-foreground">
                  Attempt level only. Per-connection outcomes are produced by the Platform and
                  read live, never copied into this record.
                </p>
              </ConceptPanel>

              <ConceptPanel
                icon={Radar}
                eyebrow="Connected now"
                title="What the runtime reports"
                testId="demo-concept-actual"
                meaning={view.actual.meaning}
                badge={<StatusBadge status={view.actual.badge} label={view.actual.label} />}
              >
                <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border/60 pt-3" data-testid="demo-actual-counts">
                  <Count value={view.counts.reported} label="reported" testId="demo-count-reported" />
                  <Count value={view.counts.connected} label="connected" testId="demo-count-connected" />
                  <Count value={view.counts.attention} label="needing attention" testId="demo-count-attention" />
                  <Count value={view.counts.noOutcome} label="no outcome" testId="demo-count-no-outcome" />
                </div>
                <dl className="mt-3 divide-y divide-border/60 border-t border-border/60">
                  <Fact label="Attached profile" value={view.actual.profileId} mono />
                  <Fact
                    label="Attached definition"
                    value={view.actual.definition}
                    mono
                    testId="demo-actual-definition"
                  />
                  <Fact label="Attached at" value={view.actual.attachedAt} />
                </dl>
                {view.actual.reason && (
                  <p className={`mt-2 text-xs ${TONE_TEXT[view.actual.tone] || TONE_TEXT.muted}`} data-testid="demo-actual-reason">
                    Reason: {view.actual.reason}
                    {view.actual.detail ? ` — ${view.actual.detail}` : ''}
                  </p>
                )}
              </ConceptPanel>
            </div>

            {/* ── drift: TWO axes, three states each, and NO control ────
                A different profile is a wrong selection; a different version of
                the right profile is a definition that moved. Different operator
                actions, so different rows — and `profile_drift` cannot see the
                second, because both sides name the same id.

                THERE IS NO BUTTON HERE, DELIBERATELY. `drift_is_advisory` is
                the contract saying neither field authorises anything: repair
                re-applies the FROZEN version, and a runtime whose profile has
                moved on refuses rather than applying the newer definition. An
                "update to latest" control would offer an operation the backend
                declines, off a reading that was never permission. */}
            <div
              className="mt-3 rounded-xl border border-border px-4 py-3"
              data-testid="demo-connections-drift"
            >
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Profile drift
                  </p>
                  <p
                    className={`mt-0.5 text-sm font-medium ${TONE_TEXT[view.drift.tone] || TONE_TEXT.muted}`}
                    data-testid="demo-drift-label"
                  >
                    {view.drift.label}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{view.drift.detail}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Version drift
                  </p>
                  <p
                    className={`mt-0.5 text-sm font-medium ${TONE_TEXT[view.versionDrift.tone] || TONE_TEXT.muted}`}
                    data-testid="demo-version-drift-label"
                  >
                    {view.versionDrift.label}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{view.versionDrift.detail}</p>
                </div>
              </div>
              <p
                className="mt-3 border-t border-border/60 pt-2 text-xs text-muted-foreground"
                data-testid="demo-drift-advisory"
              >
                {view.driftAdvisory.note}
              </p>
            </div>

            {/* ── the items, from the response and nowhere else ───────── */}
            <div className="mt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Approved connections
                {view.desired.itemCount !== null && (
                  <span className="ml-2 font-normal normal-case tracking-normal">· {view.desired.itemCount}</span>
                )}
              </h4>
              {view.items.length > 0 ? (
                <div className="mt-2">
                  <ItemList items={view.items} testIdPrefix="demo-item" />
                </div>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground" data-testid="demo-connections-no-items">
                  {view.desired.state === 'recorded'
                    ? 'The approval recorded no connections for this tenant.'
                    : 'No approved connection list is available to reconcile against — see Approved, above. An absent list is not an empty one.'}
                </p>
              )}
            </div>

            {view.undesiredItems.length > 0 && (
              <div className="mt-5" data-testid="demo-connections-undesired">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Attached without being approved
                  <span className="ml-2 font-normal normal-case tracking-normal">· {view.undesiredItems.length}</span>
                </h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  The runtime reports these and no approval asked for them. Shown rather than
                  dropped: hiding them would make the counts above disagree with the runtime.
                </p>
                <div className="mt-2">
                  <ItemList items={view.undesiredItems} testIdPrefix="demo-undesired" />
                </div>
              </div>
            )}

            {/* ── the limit of what this screen can know ──────────────── */}
            <p className="mt-4 text-xs text-muted-foreground" data-testid="demo-connections-scope-note">
              Every outcome above is the environment runtime&apos;s own last word on one
              connection. Whether Cluvik&apos;s own path through a connection works end to end
              is measured by nothing that answers this screen, and is not claimed here.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The fetching half.
 *
 * A failed request sets `error` and leaves `payload` null — the two are kept
 * apart all the way to the render, because "the request failed" collapsing into
 * "there are no connections" is the failure this whole area exists to prevent.
 */
export default function DemoConnectionsCard({
  tenantId, tenant, onTenantChanged, notify, canEdit = true, permissionReason = null,
}) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/tenants/${tenantId}/demo-connections`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      setPayload(data);
    } catch (err) {
      setError(describeApiError(err, 'Could not read Demo Connections for this tenant'));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const view = describeTenantDemoConnections({ loading, error, payload });

  // The switch reads the TENANT document's decision (`sample_credentials`,
  // `sample_connection_profile_id`, `environment` from the detail read) —
  // desired state — and after a change asks the host to re-read the tenant
  // and this card to re-read actual state, so both surfaces move together.
  const control = tenant ? (
    <DemoConnectionsSwitch
      tenantId={tenantId}
      environment={tenant.environment}
      enabled={tenant.sample_credentials === true}
      profileId={tenant.sample_connection_profile_id}
      notify={notify}
      canEdit={canEdit}
      permissionReason={permissionReason}
      onChanged={() => { onTenantChanged?.(); load(); }}
    />
  ) : null;

  return (
    <DemoConnectionsView
      view={view}
      onRefresh={load}
      managedServices={tenant?.managed_services}
      control={control}
    />
  );
}
